import React, { useState } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Button, TextField,
  IconButton, Tabs, Tab, Chip, Switch, FormControlLabel, Dialog,
  DialogTitle, DialogContent, DialogActions, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, List, ListItem,
  ListItemText, ListItemSecondaryAction, ListItemIcon, Divider, Snackbar, Alert,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { Web, Article, Menu, Image, Add, Edit, Delete, Close, Visibility, DragHandle, Save, Launch } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';

const mockPages = [
  { id: 1, title: 'Home', slug: '/', status: 'published', lastModified: '2024-01-15', template: 'Home' },
  { id: 2, title: 'About Us', slug: '/about', status: 'published', lastModified: '2024-01-10', template: 'Standard' },
  { id: 3, title: 'Admissions', slug: '/admissions', status: 'published', lastModified: '2024-01-12', template: 'Standard' },
  { id: 4, title: 'Contact', slug: '/contact', status: 'published', lastModified: '2024-01-08', template: 'Contact' },
  { id: 5, title: 'Apply Now', slug: '/apply', status: 'published', lastModified: '2024-01-18', template: 'Form' },
  { id: 6, title: 'Faith & Values', slug: '/faith', status: 'draft', lastModified: '2024-01-05', template: 'Standard' },
  { id: 7, title: 'Curriculum', slug: '/curriculum', status: 'draft', lastModified: '2024-01-03', template: 'Standard' },
];

const mockNavItems = [
  { id: 1, label: 'Home', url: '/', order: 1, visible: true },
  { id: 2, label: 'About', url: '/about', order: 2, visible: true },
  { id: 3, label: 'Admissions', url: '/admissions', order: 3, visible: true },
  { id: 4, label: 'Apply Now', url: '/apply', order: 4, visible: true },
  { id: 5, label: 'Contact', url: '/contact', order: 5, visible: true },
  { id: 6, label: 'Student Portal', url: '/student-portal', order: 6, visible: true },
  { id: 7, label: 'Parent Portal', url: '/parent-portal', order: 7, visible: true },
];

const mockMedia = [
  { id: 1, name: 'main-logo.png', type: 'image/png', size: '45 KB', uploaded: '2024-01-01', url: '/eagle-logo-transparent.png' },
  { id: 2, name: 'campus-photo.jpg', type: 'image/jpeg', size: '1.2 MB', uploaded: '2024-01-05', url: '' },
  { id: 3, name: 'student-handbook.pdf', type: 'application/pdf', size: '2.4 MB', uploaded: '2023-08-20', url: '' },
  { id: 4, name: 'chapel-service.jpg', type: 'image/jpeg', size: '890 KB', uploaded: '2024-01-10', url: '' },
  { id: 5, name: 'science-fair-2024.jpg', type: 'image/jpeg', size: '1.4 MB', uploaded: '2024-01-15', url: '' },
  { id: 6, name: 'enrollment-form.pdf', type: 'application/pdf', size: '340 KB', uploaded: '2023-08-18', url: '' },
];

const WebsiteAdmin = () => {
  const location = useLocation();
  const getInitialTab = () => {
    if (location.pathname.includes('/pages')) return 0;
    if (location.pathname.includes('/announcements')) return 1;
    if (location.pathname.includes('/navigation')) return 2;
    if (location.pathname.includes('/media')) return 3;
    return 0;
  };

  const [tab, setTab] = useState(getInitialTab());
  const [pages, setPages] = useState(mockPages);
  const [navItems, setNavItems] = useState(mockNavItems);
  const [pageDialog, setPageDialog] = useState({ open: false, item: null });
  const [pageForm, setPageForm] = useState({ title: '', slug: '', template: 'Standard', status: 'draft' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [homepageContent, setHomepageContent] = useState({
    heroTitle: 'Welcome to Eagles Refuge Academy',
    heroSubtitle: 'Nurturing Faith, Excellence, and Character in Every Student',
    heroCTA: 'Apply Now',
    missionText: 'Eagles Refuge Academy is a Christ-centered school committed to academic excellence and spiritual growth.',
    contactPhone: '(512) 555-0100',
    contactEmail: 'info@eaglesrefugeacademy.edu',
  });

  const handleSavePage = () => {
    if (pageDialog.item) {
      setPages(prev => prev.map(p => p.id === pageDialog.item.id ? { ...p, ...pageForm, lastModified: new Date().toISOString().split('T')[0] } : p));
      setSnackbar({ open: true, message: 'Page updated', severity: 'success' });
    } else {
      setPages(prev => [...prev, { id: Date.now(), ...pageForm, lastModified: new Date().toISOString().split('T')[0] }]);
      setSnackbar({ open: true, message: 'Page created', severity: 'success' });
    }
    setPageDialog({ open: false, item: null });
  };

  const toggleNavItem = (id) => {
    setNavItems(prev => prev.map(n => n.id === id ? { ...n, visible: !n.visible } : n));
  };

  const handleSaveNav = () => setSnackbar({ open: true, message: 'Navigation saved', severity: 'success' });
  const handleSaveHome = () => setSnackbar({ open: true, message: 'Homepage content saved', severity: 'success' });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Website Management</Typography>
          <Typography variant="body2" color="text.secondary">Manage website pages, content, navigation, and media</Typography>
        </Box>
        <Button variant="outlined" startIcon={<Launch />} onClick={() => window.open('/', '_blank')}>View Live Site</Button>
      </Box>

      <Paper elevation={2} sx={{ borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
            <Tab icon={<Article />} label="Pages & Content" iconPosition="start" />
            <Tab icon={<Web />} label="Homepage Content" iconPosition="start" />
            <Tab icon={<Menu />} label="Navigation" iconPosition="start" />
            <Tab icon={<Image />} label="Media Library" iconPosition="start" />
          </Tabs>
        </Box>

        {/* Pages Tab */}
        {tab === 0 && (
          <Box>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" startIcon={<Add />} onClick={() => { setPageForm({ title: '', slug: '', template: 'Standard', status: 'draft' }); setPageDialog({ open: true, item: null }); }}>
                New Page
              </Button>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Page Title</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>URL</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Template</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Last Modified</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pages.map(page => (
                    <TableRow key={page.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{page.title}</TableCell>
                      <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', px: 1, py: 0.5, borderRadius: 1 }}>{page.slug}</Typography></TableCell>
                      <TableCell><Chip label={page.template} size="small" variant="outlined" /></TableCell>
                      <TableCell><Chip label={page.status === 'published' ? 'Published' : 'Draft'} color={page.status === 'published' ? 'success' : 'default'} size="small" /></TableCell>
                      <TableCell>{page.lastModified}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton size="small" onClick={() => { setPageForm({ title: page.title, slug: page.slug, template: page.template, status: page.status }); setPageDialog({ open: true, item: page }); }}><Edit fontSize="small" /></IconButton>
                          <IconButton size="small" onClick={() => window.open(page.slug, '_blank')}><Visibility fontSize="small" /></IconButton>
                          {page.status === 'draft' && (
                            <IconButton size="small" color="error" onClick={() => setPages(prev => prev.filter(p => p.id !== page.id))}><Delete fontSize="small" /></IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Homepage Content Tab */}
        {tab === 1 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Homepage Content</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary', textTransform: 'uppercase' }}>Hero Section</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}><TextField label="Hero Headline" fullWidth value={homepageContent.heroTitle} onChange={e => setHomepageContent(p => ({ ...p, heroTitle: e.target.value }))} /></Grid>
                  <Grid item xs={12} sm={8}><TextField label="Hero Subtitle" fullWidth value={homepageContent.heroSubtitle} onChange={e => setHomepageContent(p => ({ ...p, heroSubtitle: e.target.value }))} /></Grid>
                  <Grid item xs={12} sm={4}><TextField label="Call-to-Action Button" fullWidth value={homepageContent.heroCTA} onChange={e => setHomepageContent(p => ({ ...p, heroCTA: e.target.value }))} /></Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary', textTransform: 'uppercase' }}>Mission Statement</Typography>
                <TextField label="Mission / About Text" multiline rows={3} fullWidth value={homepageContent.missionText} onChange={e => setHomepageContent(p => ({ ...p, missionText: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary', textTransform: 'uppercase' }}>Contact Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}><TextField label="Phone Number" fullWidth value={homepageContent.contactPhone} onChange={e => setHomepageContent(p => ({ ...p, contactPhone: e.target.value }))} /></Grid>
                  <Grid item xs={12} sm={6}><TextField label="Email Address" fullWidth value={homepageContent.contactEmail} onChange={e => setHomepageContent(p => ({ ...p, contactEmail: e.target.value }))} /></Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" startIcon={<Save />} onClick={handleSaveHome}>Save Homepage Content</Button>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Navigation Tab */}
        {tab === 2 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Main Navigation Menu</Typography>
              <Button variant="outlined" size="small" startIcon={<Add />}>Add Menu Item</Button>
            </Box>
            <Paper variant="outlined" sx={{ borderRadius: 2 }}>
              <List>
                {navItems.map((item, i) => (
                  <React.Fragment key={item.id}>
                    <ListItem sx={{ py: 1.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}><DragHandle color="disabled" /></ListItemIcon>
                      <ListItemText
                        primary={<Typography variant="body1" sx={{ fontWeight: 500 }}>{item.label}</Typography>}
                        secondary={<Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{item.url}</Typography>}
                      />
                      <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Switch size="small" checked={item.visible} onChange={() => toggleNavItem(item.id)} />
                        <IconButton size="small"><Edit fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" disabled={item.label === 'Home'}><Delete fontSize="small" /></IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    {i < navItems.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" startIcon={<Save />} onClick={handleSaveNav}>Save Navigation</Button>
            </Box>
          </Box>
        )}

        {/* Media Library Tab */}
        {tab === 3 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Media Library</Typography>
              <Button variant="contained" startIcon={<Add />} onClick={() => setSnackbar({ open: true, message: 'File upload dialog would open here', severity: 'info' })}>Upload File</Button>
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>File Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Size</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Uploaded</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mockMedia.map(file => (
                    <TableRow key={file.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {file.type.startsWith('image') ? <Image color="primary" /> : <Article color="error" />}
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{file.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell><Chip label={file.type.split('/')[1].toUpperCase()} size="small" variant="outlined" /></TableCell>
                      <TableCell>{file.size}</TableCell>
                      <TableCell>{file.uploaded}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton size="small"><Visibility fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={() => setSnackbar({ open: true, message: `${file.name} deleted`, severity: 'warning' })}><Delete fontSize="small" /></IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>

      {/* Page Edit Dialog */}
      <Dialog open={pageDialog.open} onClose={() => setPageDialog({ open: false, item: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {pageDialog.item ? 'Edit Page' : 'New Page'}
          <IconButton size="small" onClick={() => setPageDialog({ open: false, item: null })}><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}><TextField label="Page Title" fullWidth value={pageForm.title} onChange={e => setPageForm(p => ({ ...p, title: e.target.value }))} required /></Grid>
            <Grid item xs={12}><TextField label="URL Slug" fullWidth value={pageForm.slug} onChange={e => setPageForm(p => ({ ...p, slug: e.target.value }))} helperText="e.g. /about" /></Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Template</InputLabel>
                <Select value={pageForm.template} label="Template" onChange={e => setPageForm(p => ({ ...p, template: e.target.value }))}>
                  <MenuItem value="Standard">Standard</MenuItem>
                  <MenuItem value="Home">Home</MenuItem>
                  <MenuItem value="Form">Form</MenuItem>
                  <MenuItem value="Contact">Contact</MenuItem>
                  <MenuItem value="Landing">Landing Page</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={pageForm.status} label="Status" onChange={e => setPageForm(p => ({ ...p, status: e.target.value }))}>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="published">Published</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setPageDialog({ open: false, item: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleSavePage} disabled={!pageForm.title}>{pageDialog.item ? 'Save Changes' : 'Create Page'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(p => ({ ...p, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default WebsiteAdmin;
